import {
  Box,
  Chevron,
  DropdownMenu,
  DropdownMenuCardItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Grid,
  Icon,
  IconTile,
  Inline,
  NavButton,
  Stack,
  Text,
} from "@generalapplications/ui";
import { ArrowRight } from "lucide-react";

import type { SectionPage } from "@/app/common/functions/routes/sectionNavigation";

export type SectionMenuProps = {
  label: string;
  /** The section's overview page, the footer's link. */
  href: string;
  pages: readonly SectionPage[];
  active: boolean;
  /** Which edge of the trigger the menu lines up with. */
  align: "start" | "end";
};

/**
 * A desktop section, PlantHarvester's Library pattern: a plain trigger in the
 * bar; inside, every page a card with its mark on a coloured tile, its title
 * and a line on it, two columns; a footer with the count on the left and the
 * overview link on the right. The marks and colours live here, not in the bar.
 */
export function SectionMenu({
  label,
  href,
  pages,
  active,
  align,
}: SectionMenuProps) {
  const noun = label.toLowerCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <NavButton active={active} aria-current={active ? "page" : undefined}>
          {label}
          <Chevron />
        </NavButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} width="xl">
        <Grid columns={2} gap="xs">
          {pages.map((page) => (
            <DropdownMenuCardItem
              key={page.href}
              href={page.href}
              data-tone={page.tone}
            >
              <IconTile icon={page.icon} tone="section" />
              <Stack gap="xs">
                <Text as="span" variant="body" weight="semibold">
                  {page.title}
                </Text>
                <Text as="span">{page.description}</Text>
              </Stack>
            </DropdownMenuCardItem>
          ))}
        </Grid>
        <DropdownMenuSeparator />
        {/* The library has no menu footer part; this is one from its
            layout and text parts, as PlantHarvester composes it. */}
        <Box paddingX="md" paddingBottom="xs">
          <Text as="div">
            <Inline wrap justify="between" gap="sm" align="center">
              <Text as="span" variant="label" tone="muted" numeric>
                {pages.length} {noun}
              </Text>
              <DropdownMenuItem tone="primary" href={href}>
                {label} overview
                <Icon icon={ArrowRight} size="sm" />
              </DropdownMenuItem>
            </Inline>
          </Text>
        </Box>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
